import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileAudio, TrendingUp, Cpu, Download, ChevronRight } from 'lucide-react';
import { adminApi } from '../utils/api';
import Layout from '../components/Layout';
import Header from '../components/Header';
import MaintenanceControl from '../components/MaintenanceControl';
import { Button, Card, Select, Badge, Spinner, ErrorState } from '../components/ui';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { planToBadgeVariant } from '../utils/statusBadge';
import type { User, PlanType } from '../types';

interface AdminPageProps {
  user: User;
}

interface PlanStats {
  plan_type: PlanType;
  user_count: number;
  total_revenue: number;
}

interface MonthlyRevenue {
  month: string;
  total_revenue: number;
  total_cost: number;
  gpu_cost: number;
  gpu_cost_is_actual: boolean;
  user_count: number;
  transcription_count: number;
}

interface AdminStats {
  total_users: number;
  active_users: number;
  total_transcriptions: number;
  total_hours_processed: number;
  monthly_revenue: number;
  monthly_cost: number;
  monthly_cost_breakdown: { gpu_source: 'runpod_actual' | 'estimated' };
  monthly_profit: number;
  plan_stats: PlanStats[];
  average_processing_ratio: number;
  total_gpu_hours: number;
  monthly_processing_hours: number;
  monthly_history: MonthlyRevenue[];
  service_launch_month: string;
}

const PLAN_LABELS: Record<PlanType, string> = {
  free: '無料',
  lite: 'ライト',
  standard: 'スタンダード',
  unlimited: '無制限',
};

export default function AdminPage({ user }: AdminPageProps) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load admin stats:', err);
      setError('統計データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const blob = await adminApi.exportRevenue(exportYear, exportMonth);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `otomochi_revenue_${exportYear}${String(exportMonth).padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('CSVエクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  const planColumns: Column<PlanStats>[] = [
    { key: 'plan', header: 'プラン', render: (p) => <Badge variant={planToBadgeVariant(p.plan_type)}>{PLAN_LABELS[p.plan_type]}</Badge> },
    { key: 'users', header: 'ユーザー数', align: 'right', render: (p) => <span className="font-medium">{p.user_count}人</span> },
    { key: 'rev', header: '収益', align: 'right', render: (p) => <span className="font-medium">¥{p.total_revenue.toLocaleString()}</span> },
  ];

  const monthlyColumns: Column<MonthlyRevenue>[] = [
    { key: 'month', header: '月', render: (m) => m.month },
    { key: 'rev', header: '収益', align: 'right', render: (m) => <span className="text-success font-medium">¥{m.total_revenue.toLocaleString()}</span> },
    { key: 'cost', header: 'コスト', align: 'right', render: (m) => <span className="text-danger font-medium">${m.total_cost.toFixed(2)}</span> },
    { key: 'count', header: '書き起こし数', align: 'right', render: (m) => `${m.transcription_count}件` },
  ];

  return (
    <Layout>
      <Header user={user} title="otomochi - 管理者" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold mb-8">管理者ダッシュボード</h2>

        {/* 統計の読み込み状態に関係なく常に出す（バックエンド不調時こそメンテを出したいため） */}
        <div className="mb-8">
          <MaintenanceControl />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Spinner size="lg" className="mx-auto" />
            <p className="mt-4 text-text-sub">読み込み中...</p>
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={loadStats} />
        ) : stats ? (
          <div className="space-y-8">
            {/* クイックアクション */}
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/admin/users" className="block">
                <Card interactive className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-primary/20 rounded-lg">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-bold">ユーザー管理</div>
                      <div className="text-sm text-text-sub">{stats.total_users}人のユーザー</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-sub" />
                </Card>
              </Link>

              <Link to="/admin/transcriptions" className="block">
                <Card interactive className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-info-bg rounded-lg">
                      <FileAudio className="w-6 h-6 text-info" />
                    </div>
                    <div>
                      <div className="font-bold">書き起こし管理</div>
                      <div className="text-sm text-text-sub">{stats.total_transcriptions}件の書き起こし</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-sub" />
                </Card>
              </Link>

              <Link to="/admin/revenue" className="block">
                <Card interactive className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-success-bg rounded-lg">
                      <TrendingUp className="w-6 h-6 text-success" />
                    </div>
                    <div>
                      <div className="font-bold">収益レポート</div>
                      <div className="text-sm text-text-sub">月次収益を確認</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-sub" />
                </Card>
              </Link>
            </div>

            {/* メイン統計 */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-text-sub" />
                  <span className="text-sm text-text-sub">総ユーザー数</span>
                </div>
                <div className="text-3xl font-bold text-primary">{stats.total_users}</div>
                <div className="text-sm text-text-sub mt-1">アクティブ: {stats.active_users}人</div>
              </Card>

              <Card>
                <div className="flex items-center gap-3 mb-2">
                  <FileAudio className="w-5 h-5 text-text-sub" />
                  <span className="text-sm text-text-sub">総書き起こし数</span>
                </div>
                <div className="text-3xl font-bold text-primary">{stats.total_transcriptions}</div>
              </Card>

              <Card>
                <div className="flex items-center gap-3 mb-2">
                  <Cpu className="w-5 h-5 text-text-sub" />
                  <span className="text-sm text-text-sub">処理時間合計</span>
                </div>
                <div className="text-3xl font-bold text-primary">{stats.total_hours_processed.toFixed(1)}h</div>
                <div className="text-sm text-text-sub mt-1">
                  今月GPU: {stats.total_gpu_hours.toFixed(2)}h
                  {stats.monthly_cost_breakdown.gpu_source === 'estimated' && '（推定）'}
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-text-sub" />
                  <span className="text-sm text-text-sub">処理速度比</span>
                </div>
                <div className="text-3xl font-bold text-primary">{stats.average_processing_ratio.toFixed(1)}x</div>
                <div className="text-sm text-text-sub mt-1">音声時間/処理時間</div>
              </Card>
            </div>

            {/* 収益 */}
            <Card>
              <h3 className="text-xl font-bold mb-4">今月の収益</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-success-bg rounded-field">
                  <div className="text-sm text-text-sub mb-1">売上</div>
                  <div className="text-3xl font-bold text-success">¥{stats.monthly_revenue.toLocaleString()}</div>
                </div>
                <div className="text-center p-4 bg-danger-bg rounded-field">
                  <div className="text-sm text-text-sub mb-1">
                    コスト (GPU + 固定費)
                    {stats.monthly_cost_breakdown.gpu_source === 'estimated' && ' ※GPUは推定'}
                  </div>
                  <div className="text-3xl font-bold text-danger">${stats.monthly_cost.toFixed(2)}</div>
                </div>
                <div className="text-center p-4 bg-info-bg rounded-field">
                  <div className="text-sm text-text-sub mb-1">利益 (USD換算)</div>
                  <div className={`text-3xl font-bold ${stats.monthly_profit >= 0 ? 'text-info' : 'text-danger'}`}>
                    ${stats.monthly_profit.toFixed(2)}
                  </div>
                </div>
              </div>
            </Card>

            {/* プラン別統計 */}
            <Card>
              <h3 className="text-xl font-bold mb-4">プラン別ユーザー</h3>
              <ResponsiveTable
                columns={planColumns}
                rows={stats.plan_stats}
                rowKey={(p) => p.plan_type}
                caption="プラン別ユーザー"
              />
            </Card>

            {/* 月次推移 */}
            <Card>
              <h3 className="text-xl font-bold mb-4">月次推移</h3>
              <ResponsiveTable
                columns={monthlyColumns}
                rows={stats.monthly_history}
                rowKey={(m) => m.month}
                caption="月次推移"
                emptyMessage={`${stats.service_launch_month} の公開以降に実績が入ります`}
              />
            </Card>

            {/* CSVエクスポート */}
            <Card>
              <h3 className="text-xl font-bold mb-4">データエクスポート</h3>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label htmlFor="admin-export-year" className="block text-sm text-text-sub mb-1">年</label>
                  <div className="w-32">
                    <Select id="admin-export-year" value={exportYear} onChange={(e) => setExportYear(Number(e.target.value))}>
                      {[2024, 2025, 2026].map((year) => (
                        <option key={year} value={year}>{year}年</option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <label htmlFor="admin-export-month" className="block text-sm text-text-sub mb-1">月</label>
                  <div className="w-32">
                    <Select id="admin-export-month" value={exportMonth} onChange={(e) => setExportMonth(Number(e.target.value))}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <option key={month} value={month}>{month}月</option>
                      ))}
                    </Select>
                  </div>
                </div>
                <Button onClick={handleExportCSV} loading={exporting} className="gap-2">
                  {exporting ? (
                    'エクスポート中...'
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      収益CSVをダウンロード
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm text-text-sub mt-4">
                確定申告用の収益・コストデータをCSV形式でダウンロードできます。
              </p>
            </Card>
          </div>
        ) : null}
      </main>
    </Layout>
  );
}
